import {
  openToolbar,
  closeToolbar,
  historicSave,
  saveActiveSlideData,
  addImageObject,
} from 'features/workspace/canvasSlice';
import { CanvasToolbarEnum, UserTypeEnum, WorkspaceModalsEnum } from 'enums';
import store from 'features/workspace/store';
import { setRemoveWatermark, setWatermarkPosition } from 'features/workspace/trialSlice';
import { debounce, throttle } from 'lodash';
import { fabric } from 'fabric';
import { open } from 'features/workspace/boardMenuSlice';
import {
  closeSnackbar,
  openModal,
  setBusy,
  setModalData,
  setNotBusy,
  showSnackbar,
} from 'features/workspace/generalSlice';
import { blackNovemberPromotionData, fileToBase64, isAnniversaryActive, makeImageUrl } from 'utils';
import { getImageDimensionsFromBase64 } from './image';
import UploadsService from 'services/UploadsService';
import ReactGA from 'react-ga4';
import getCleverTap from 'utils/clevertap';
import { TRIAL_UPLOAD_LIMIT } from 'config/workspace';
import * as mime from 'mime-types';

function checkLockedObjects(instance, event) {
  const activeObject = instance.getActiveObject();

  const isLocked = activeObject.get('lockMovementX') || activeObject.get('lockMovementY');
  if (isLocked) {
    activeObject.set({
      editable: false,
      borderColor: '#9e9e9e',
      cornerColor: '#9e9e9e',
    });

    activeObject.setControlsVisibility({
      mtr: true,
      lockControl: true,
    });
  }
}

function handleObjectRotating(event) {
  event.target.handleRotation();
}

function handleObjectMoving(instance, event) {
  /* eslint-disable-next-line eqeqeq */
  if (event.target.type == 'DynamicLine') {
    setControlPoints(instance, event.target);
  }

  const grid = store.getState().canvas.grid;
  const isGridEnabled = grid?.open ?? false;

  if (isGridEnabled) {
    const gridSize = grid.size;
    event.target.set({
      left: Math.round(event.target.left / gridSize) * gridSize,
      top: Math.round(event.target.top / gridSize) * gridSize,
    });
  }
}

/**
 * A debounced function that saves the active slide data.
 * This function is exported and used to save the state of the canvas.
 * It is debounced to ensure that it only saves after 5 seconds of inactivity.
 *
 * @returns {Promise} A promise that resolves when the data is successfully saved.
 */
export const debouncedSaveActiveSlideData = debounce(
  () => store.dispatch(saveActiveSlideData()).unwrap(),
  5000,
);

const throttledTrackClientMemoryUsage = throttle(trackClientMemoryUsage, 10000);

function handleObjectModified() {
  debouncedSaveActiveSlideData();

  if (performance?.memory && window.DD_RUM) {
    throttledTrackClientMemoryUsage(); // Send JS heap stats to Datadog
  }

  setTimeout(() => {
    store.dispatch(historicSave());
  }, 100);
}

function trackClientMemoryUsage() {
  const percent = Math.round(
    (performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit) * 100,
  );
  const usedMB = Math.round(performance.memory.usedJSHeapSize / 1048576);
  const limitMB = Math.round(performance.memory.jsHeapSizeLimit / 1048576);

  window.DD_RUM.addAction('js_heap_stats', {
    percent,
    usedMB,
    limitMB,
    event: 'object:modified',
  });
}

function updateToolbar(activeObject) {
  const activeToolbar = store.getState().canvas.activeToolbar;

  if (!activeObject && activeToolbar) {
    store.dispatch(closeToolbar());
  }
  if (!activeObject) return;

  if ([CanvasToolbarEnum.ACTIVE_SELECTION, CanvasToolbarEnum.GROUP].includes(activeObject.type)) {
    const checkObjectsType = (object, type) =>
      object.getObjects().map((object) => {
        if (object.type === CanvasToolbarEnum.GROUP) {
          return checkObjectsType(object, type);
        }

        if (object.type !== type) {
          return false;
        }
        return true;
      });

    const objectsType = checkObjectsType(activeObject, CanvasToolbarEnum.TEXT).flat(Infinity);
    if (!objectsType.includes(false)) {
      store.dispatch(closeToolbar());
      setTimeout(() => {
        store.dispatch(openToolbar(CanvasToolbarEnum.TEXT));
      }, 100);
      return;
    }
  }

  if (
    activeToolbar === activeObject.imgType ||
    (activeObject.type && activeObject.imgType !== 'MTGCrop')
  ) {
    store.dispatch(closeToolbar());
  }

  setTimeout(() => {
    if (activeObject.imgType !== 'MTGCrop') {
      if (activeObject.type === CanvasToolbarEnum.TEXT) {
        store.dispatch(openToolbar(CanvasToolbarEnum.TEXT));
      } else {
        store.dispatch(openToolbar(activeObject.imgType || activeObject.type));
      }
    }
  }, 100);

  if (activeObject.imgType?.startsWith('comment')) {
    store.dispatch(open('comments'));
  }
}

function handleWatermarksOnSelection(instance, activeObject) {
  const watermarks = instance.getObjects().filter(
    (watermark) =>
      watermark.watermarkTo &&
      instance
        .getActiveObjects()
        .map((object) => object.uuid)
        .includes(watermark.watermarkTo),
  );

  watermarks.forEach((watermark) => {
    activeObject.addWithUpdate(watermark);
  });
}

function handleSelection(instance) {
  const activeObject = instance.getActiveObject();
  if (
    store.getState().general.creation.tp_user === UserTypeEnum.TRIAL &&
    activeObject.get('type') === CanvasToolbarEnum.ACTIVE_SELECTION
  ) {
    handleWatermarksOnSelection(instance, activeObject);
  }

  updateToolbar(activeObject);

  /* eslint-disable-next-line eqeqeq */
  if (activeObject.type == 'DynamicLine') {
    setControlPoints(instance, activeObject);
  }
}

function onClearSelection(instance) {
  updateToolbar(null);

  instance.getObjects().forEach(function (obj) {
    if (obj.controlPoint) {
      instance.remove(obj);
    }
  });
}

export function setControlPoints(instance, activeObject) {
  // remove old control points
  instance.getObjects().forEach(function (obj) {
    if (obj.controlPoint) instance.remove(obj);
  });
  let controlPoints = [];

  const isFlipped = activeObject.get('flipX');

  activeObject.path.forEach(function (point, index) {
    const numberOfPoints = (point.length - 1) / 2;
    for (let i = 0; i < numberOfPoints; i++) {
      const controlPoint = new fabric.Circle({
        radius: 6,
        left: isFlipped
          ? activeObject.left + activeObject.width / 2 - point[1 + i * 2] - 5
          : activeObject.left + activeObject.width / 2 + point[1 + i * 2] - 5,
        top: activeObject.top + activeObject.height / 2 + point[2 + i * 2] - 5,
        fill: '#8FC1BC',
        hasBorders: false,
        hasControls: false,
        controlPoint: true,
      });

      controlPoints.push(controlPoint);
      instance.add(controlPoint);

      controlPoint.on('moving', function (e) {
        activeObject.path[index][1 + i * 2] = isFlipped
          ? activeObject.path[index][1 + i * 2] - e.e.movementX / instance.getZoom()
          : activeObject.path[index][1 + i * 2] + e.e.movementX / instance.getZoom();
        activeObject.path[index][2 + i * 2] =
          activeObject.path[index][2 + i * 2] + e.e.movementY / instance.getZoom();
        activeObject.setCoords();
        instance.requestRenderAll();
      });

      controlPoint.on('mouseup', function () {
        // Calculate the actual curve points including control points and endpoints
        const allPoints = calculateCurvePoints(activeObject, isFlipped);

        // Find the true bounding box that encompasses the entire curve
        const boundingBox = calculateTrueBoundingBox(allPoints);

        const newLeft = boundingBox.left;
        const newTop = boundingBox.top;
        const newWidth = boundingBox.width;
        const newHeight = boundingBox.height;
        const newCenterX = newLeft + newWidth / 2;
        const newCenterY = newTop + newHeight / 2;

        // Update object bounds
        activeObject.set({
          top: newTop,
          left: newLeft,
          width: newWidth,
          height: newHeight,
        });

        // Recalculate all path coordinates relative to the new bounds
        recalculatePathCoordinates(activeObject, allPoints, newCenterX, newCenterY, isFlipped);

        activeObject.setCoords();
        instance.requestRenderAll();
      });
    }
  });
}

// Calculate all points along the curve for more accurate bounding box
function calculateCurvePoints(activeObject, isFlipped) {
  const allPoints = [];

  activeObject.path.forEach((point) => {
    const command = point[0];

    if (command === 'M' || command === 'L') {
      // For move and line commands
      const x = isFlipped
        ? activeObject.left + activeObject.width / 2 - point[1]
        : activeObject.left + activeObject.width / 2 + point[1];
      const y = activeObject.top + activeObject.height / 2 + point[2];

      allPoints.push({ x, y });
    } else if (command === 'Q') {
      // For quadratic Bézier curves, include control point and endpoint
      const startX = allPoints.length > 0 ? allPoints[allPoints.length - 1].x : 0;
      const startY = allPoints.length > 0 ? allPoints[allPoints.length - 1].y : 0;

      const controlX = isFlipped
        ? activeObject.left + activeObject.width / 2 - point[1]
        : activeObject.left + activeObject.width / 2 + point[1];
      const controlY = activeObject.top + activeObject.height / 2 + point[2];

      const endX = isFlipped
        ? activeObject.left + activeObject.width / 2 - point[3]
        : activeObject.left + activeObject.width / 2 + point[3];
      const endY = activeObject.top + activeObject.height / 2 + point[4];

      // Add control point
      allPoints.push({ x: controlX, y: controlY });

      // Add some intermediate points along the curve for better bounding box calculation
      for (let t = 0.1; t < 1; t += 0.1) {
        const qx = Math.pow(1 - t, 2) * startX + 2 * (1 - t) * t * controlX + Math.pow(t, 2) * endX;
        const qy = Math.pow(1 - t, 2) * startY + 2 * (1 - t) * t * controlY + Math.pow(t, 2) * endY;
        allPoints.push({ x: qx, y: qy });
      }

      // Add endpoint
      allPoints.push({ x: endX, y: endY });
    } else if (command === 'C') {
      // For cubic Bézier curves
      const startX = allPoints.length > 0 ? allPoints[allPoints.length - 1].x : 0;
      const startY = allPoints.length > 0 ? allPoints[allPoints.length - 1].y : 0;

      const control1X = isFlipped
        ? activeObject.left + activeObject.width / 2 - point[1]
        : activeObject.left + activeObject.width / 2 + point[1];
      const control1Y = activeObject.top + activeObject.height / 2 + point[2];

      const control2X = isFlipped
        ? activeObject.left + activeObject.width / 2 - point[3]
        : activeObject.left + activeObject.width / 2 + point[3];
      const control2Y = activeObject.top + activeObject.height / 2 + point[4];

      const endX = isFlipped
        ? activeObject.left + activeObject.width / 2 - point[5]
        : activeObject.left + activeObject.width / 2 + point[5];
      const endY = activeObject.top + activeObject.height / 2 + point[6];

      // Add control points
      allPoints.push({ x: control1X, y: control1Y });
      allPoints.push({ x: control2X, y: control2Y });

      // Add intermediate points along the curve
      for (let t = 0.1; t < 1; t += 0.1) {
        const cx =
          Math.pow(1 - t, 3) * startX +
          3 * Math.pow(1 - t, 2) * t * control1X +
          3 * (1 - t) * Math.pow(t, 2) * control2X +
          Math.pow(t, 3) * endX;
        const cy =
          Math.pow(1 - t, 3) * startY +
          3 * Math.pow(1 - t, 2) * t * control1Y +
          3 * (1 - t) * Math.pow(t, 2) * control2Y +
          Math.pow(t, 3) * endY;
        allPoints.push({ x: cx, y: cy });
      }

      // Add endpoint
      allPoints.push({ x: endX, y: endY });
    }
  });

  return allPoints;
}

// Calculate the true bounding box that encompasses all points
function calculateTrueBoundingBox(points) {
  if (!points.length) return { left: 0, top: 0, width: 0, height: 0 };

  let minX = points[0].x,
    maxX = points[0].x;
  let minY = points[0].y,
    maxY = points[0].y;

  points.forEach((point) => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  });

  return {
    left: minX,
    top: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

// Recalculate path coordinates relative to new center
function recalculatePathCoordinates(activeObject, allPoints, centerX, centerY, isFlipped) {
  let pointIndex = 0;

  activeObject.path.forEach((point) => {
    const command = point[0];

    if (command === 'M' || command === 'L') {
      if (isFlipped) {
        point[1] = centerX - allPoints[pointIndex].x;
      } else {
        point[1] = allPoints[pointIndex].x - centerX;
      }
      point[2] = allPoints[pointIndex].y - centerY;
      pointIndex++;
    } else if (command === 'Q') {
      // Control point
      if (isFlipped) {
        point[1] = centerX - allPoints[pointIndex].x;
      } else {
        point[1] = allPoints[pointIndex].x - centerX;
      }
      point[2] = allPoints[pointIndex].y - centerY;
      pointIndex++;

      // Skip intermediate curve points
      pointIndex += Math.floor(1 / 0.1);

      // End point
      if (isFlipped) {
        point[3] = centerX - allPoints[pointIndex].x;
      } else {
        point[3] = allPoints[pointIndex].x - centerX;
      }
      point[4] = allPoints[pointIndex].y - centerY;
      pointIndex++;
    } else if (command === 'C') {
      // First control point
      if (isFlipped) {
        point[1] = centerX - allPoints[pointIndex].x;
      } else {
        point[1] = allPoints[pointIndex].x - centerX;
      }
      point[2] = allPoints[pointIndex].y - centerY;
      pointIndex++;

      // Second control point
      if (isFlipped) {
        point[3] = centerX - allPoints[pointIndex].x;
      } else {
        point[3] = allPoints[pointIndex].x - centerX;
      }
      point[4] = allPoints[pointIndex].y - centerY;
      pointIndex++;

      // Skip intermediate curve points
      pointIndex += Math.floor(1 / 0.1);

      // End point
      if (isFlipped) {
        point[5] = centerX - allPoints[pointIndex].x;
      } else {
        point[5] = allPoints[pointIndex].x - centerX;
      }
      point[6] = allPoints[pointIndex].y - centerY;
      pointIndex++;
    }
  });
}

function getShapeCoords(shape) {
  const shapeCoords = [];
  shape.path.forEach((point) => {
    const numberOfPoints = (point.length - 1) / 2;
    for (let i = 0; i < numberOfPoints; i++) {
      shapeCoords.push({
        x: shape.left + shape.width / 2 + point[1 + i * 2],
        y: shape.top + shape.height / 2 + point[2 + i * 2],
      });
    }
  });
  return shapeCoords;
}

function optimizeTextObject(instance) {
  const obj = instance.getActiveObject();
  if (!obj || obj.type !== 'textbox') {
    return;
  }
  const topLevelStyles = {
    fontFamily: obj.fontFamily,
    fontWeight: obj.fontWeight,
    fontStyle: obj.fontStyle,
    fontSize: obj.fontSize,
    fill: obj.fill,
    underline: obj.underline,
    textBackgroundColor: obj.textBackgroundColor,
  };

  for (const lineIndex in obj.styles) {
    const line = obj.styles[lineIndex];

    for (const charIndex in line) {
      const charStyle = line[charIndex];

      for (const styleProperty in charStyle) {
        if (charStyle[styleProperty] !== topLevelStyles[styleProperty]) {
          return; // Found a different style, unable to optimize
        }
      }
    }
  }

  obj.styles = {};
}

async function handleFileUpload(instance, user, file) {
  if (!instance || !user || !file) return;
  const extensions = ['jpg', 'png', 'gif', 'webp'];
  const mimeTypes = extensions.map((extension) => mime.lookup(extension));
  if (!mimeTypes.includes(file.type)) {
    store.dispatch(
      showSnackbar({ text: 'Error. Only JPG, PNG, GIF or WEBP supported.', severity: 'error' }),
    );
    return;
  }
  const clevertap = getCleverTap();

  const sendBannerClickTrackingEvents = function (description) {
    ReactGA.event({
      category: 'workspace',
      action: 'banner_upload_limit_clicked',
      label: 'Workspace Conversion Banner Upload Limit Clicked',
      value: user.id,
    });
    clevertap.event.push('Banner Click', {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: '/workspace/',
      Description: description,
      Locale: 'workspace',
    });
  };

  const newUploadsCount =
    instance.getObjects().filter((object) => object.imgType === 'upload').length + 1;
  if (user.tp_user === UserTypeEnum.TRIAL && newUploadsCount > TRIAL_UPLOAD_LIMIT) {
    store.dispatch(openModal(WorkspaceModalsEnum.UPLOAD_LIMIT));
    return;
  }

  store.dispatch(setBusy());
  store.dispatch(showSnackbar({ text: 'Uploading image... Please wait', severity: 'processing' }));

  try {
    const content = (await fileToBase64(file)).content;
    const { width, height } = await getImageDimensionsFromBase64(content, file.type);
    const { data } = await UploadsService.create({ width, height, content });
    store.dispatch(setModalData({ refreshUploads: Date.now() }));
    const src = makeImageUrl(data.name);
    const name = src.split('/').pop().split('.')[0];

    const clevertapBaseEvent = {
      Product: 'MTG',
      Email: user.email,
      'Country code': user.country_code,
      URL: '/workspace/',
      Locale: 'workspace',
    };
    clevertap.event.push('Upload Image', {
      ...clevertapBaseEvent,
      Description: 'Upload Image',
      Name: name,
    });

    ReactGA.event({
      category: 'workspace',
      action: 'drag_uploads_component',
      label: 'Drop the component to canva action',
      value: user.id,
    });
    clevertap.event.push('Action Workspace', {
      ...clevertapBaseEvent,
      Description: 'Drag Upload Component',
      Name: name,
    });
    store.dispatch(
      addImageObject({
        imgType: 'upload',
        imgUrl: src,
        imgId: data.id,
      }),
    );
    setTimeout(() => {
      store.dispatch(historicSave());
    }, 100);

    store.dispatch(setNotBusy());
    store.dispatch(showSnackbar({ text: 'Image uploaded successfully', severity: 'success' }));
  } catch (error) {
    store.dispatch(setNotBusy());
    if (error?.response?.status === 403) {
      store.dispatch(closeSnackbar());
      if (blackNovemberPromotionData().isActive) {
        sendBannerClickTrackingEvents('Black November Upload Limit Clicked');
        store.dispatch(openModal(WorkspaceModalsEnum.BLACK_NOVEMBER));
        return;
      }

      if (isAnniversaryActive()) {
        sendBannerClickTrackingEvents('Anniversary MTG Upload Limit Clicked');
        store.dispatch(openModal(WorkspaceModalsEnum.ANNIVERSARY_MTG));
        return;
      }

      sendBannerClickTrackingEvents('Upload Limit Clicked');
      store.dispatch(openModal(WorkspaceModalsEnum.UPLOAD_LIMIT_NEW));
      return;
    }
    store.dispatch(showSnackbar({ text: 'Failed to upload file', severity: 'error' }));
  }
}

export function initListeners(instance, user) {
  const freeUser = user?.tp_user === UserTypeEnum.TRIAL;
  let movingRafId = null;
  let rotatingRafId = null;
  let scalingRafId = null;

  instance.on({
    'object:rotating': (event) => {
      handleObjectRotating(event);
      if (freeUser) {
        if (rotatingRafId) cancelAnimationFrame(rotatingRafId);
        rotatingRafId = requestAnimationFrame(() => {
          positionBtn(instance);
          rotatingRafId = null;
        });
      }
    },
    'object:moving': (event) => {
      handleObjectMoving(instance, event);
      if (freeUser) {
        if (movingRafId) cancelAnimationFrame(movingRafId);
        movingRafId = requestAnimationFrame(() => {
          positionBtn(instance);
          positionWatermark(instance, event.target);
          movingRafId = null;
        });
      }
    },
    'object:modified': () => handleObjectModified(),
    'selection:created': (event) => {
      handleSelection(instance);
      checkLockedObjects(instance, event);
      optimizeTextObject(instance);
      if (freeUser) positionBtn(instance);
    },
    'selection:updated': (event) => {
      handleSelection(instance);
      checkLockedObjects(instance, event);
      if (freeUser) positionBtn(instance);
    },
    'selection:cleared': () => {
      onClearSelection(instance);
      if (freeUser) hideBtn();
    },
    'path:created': function (e) {
      try {
        const path = e.path;
        const pathObject = new fabric.Path(path.path, {
          fill: 'transparent',
          stroke: path.stroke,
          strokeWidth: path.strokeWidth,
          strokeLineCap: 'round',
          strokeLineJoin: 'round',
          strokeUniform: true,
          objectCaching: false,
          perPixelTargetFind: true,
          targetFindTolerance: 4,
          hasControls: true,
          hasBorders: true,
          imgType: 'dynamic_shape',
        });
        instance.remove(path);
        instance.add(pathObject);
        instance.setActiveObject(pathObject);
        instance.requestRenderAll();
        instance.isDrawingMode = false;
      } catch (error) {
        console.error('Error handling path creation:', error);
      }
    },
    drop: ({ e }) => {
      e.preventDefault();
      handleFileUpload(instance, user, e.dataTransfer.files[0]);
    },
  });

  instance.on('mouse:down', (event) => {
    if (event.target && event.target._objects) {
      const activeSelection = instance.getActiveObject();
      let oneLocked = false;
      activeSelection.getObjects().forEach((obj) => {
        if (obj.lockMovementX || obj.lockMovementY) {
          activeSelection.removeWithUpdate(obj);
          oneLocked = true;
        }
      });

      if (oneLocked) {
        activeSelection.set({
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
        });
      }
      instance.requestRenderAll();
    }
  });

  if (freeUser) {
    instance.on({
      'object:scaling': (e) => {
        if (scalingRafId) cancelAnimationFrame(scalingRafId);
        scalingRafId = requestAnimationFrame(() => {
          positionBtn(instance);
          scaleWatermark(instance, e.target);
          scalingRafId = null;
        });
      },
    });

    document.getElementById('canvas-area').addEventListener('scroll', function () {
      if (instance.getActiveObject()) {
        positionBtn(instance);
      }
    });
  }
}

const debouncedPositionWatermark = debounce(function (instance, activeObject) {
  const objects = instance.getObjects();

  const watermark = objects.find(function (o) {
    return o.watermarkTo !== undefined && o.watermarkTo === activeObject.uuid;
  });

  if (watermark) {
    watermark.set({
      left: activeObject.left + (activeObject.getScaledWidth() - watermark.getScaledWidth()) / 2,
      top: activeObject.top + (activeObject.getScaledHeight() - watermark.getScaledHeight()) / 2,
    });
    instance.requestRenderAll();
  }
}, 70);

function positionWatermark(instance, activeObject) {
  debouncedPositionWatermark(instance, activeObject);
}

function scaleWatermark(instance, activeObject) {
  const objects = instance.getObjects();
  const watermark = objects.find(function (o) {
    return o.watermarkTo !== undefined && o.watermarkTo === activeObject.uuid;
  });

  if (watermark) {
    const objAspectRatio = activeObject.getScaledWidth() / activeObject.getScaledHeight();
    const watermarkAspectRatio = watermark.width / watermark.height;

    if (objAspectRatio > watermarkAspectRatio) {
      watermark.scaleToHeight(activeObject.getScaledHeight());
    } else {
      watermark.scaleToWidth(activeObject.getScaledWidth());
    }
    positionWatermark(instance, activeObject);
  }
}

const debouncedPositionBtn = debounce(function (instance) {
  const obj = instance.getActiveObject();
  if (!obj) return;
  if ([CanvasToolbarEnum.ILLUSTRATION, CanvasToolbarEnum.ICON].includes(obj.imgType)) {
    const absCoords = instance.getAbsoluteCenter(obj);
    const left = absCoords.left;
    const top = absCoords.top;
    store.dispatch(setWatermarkPosition({ left, top }));
    store.dispatch(setRemoveWatermark(true));
  } else {
    store.dispatch(setRemoveWatermark(false));
  }
}, 10);

function positionBtn(instance) {
  debouncedPositionBtn(instance);
}

function hideBtn() {
  if (store.getState().general.creation.tp_user !== UserTypeEnum.TRIAL) return;
  store.dispatch(setRemoveWatermark(false));
}
